import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

interface UseRippleAnimationOptions {
  /** 是否为待机模式 */
  entryStandbyMode?: boolean;
  /** 是否启用涟漪效果 */
  enableRipples?: boolean;
  /** 涟漪点数量 */
  dotCount?: number;
  /** 涟漪点生命周期（毫秒） */
  dotLifetime?: number;
  /** 涟漪点生成间隔（毫秒） */
  dotInterval?: number;
}

interface UseRippleAnimationReturn {
  /** 是否应该显示涟漪效果 */
  shouldShowRipples: boolean;
  /** 涟漪效果配置 */
  rippleConfig: {
    dotCount: number;
    dotLifetime: number;
    dotInterval: number;
    radiusRange: { min: number; max: number };
    sizeRange: { min: number; max: number };
    color: string;
  };
}

/**
 * 涟漪动画管理Hook
 * 根据应用状态决定是否显示涟漪效果及其配置
 */
export function useRippleAnimation({
  entryStandbyMode = false,
  enableRipples = true,
  dotCount = 16,
  dotLifetime = 1800,
  dotInterval = 120,
}: UseRippleAnimationOptions = {}): UseRippleAnimationReturn {
  const room = useSelector((state: RootState) => state.room);
  const { isAITalking, isUserTalking } = room;

  // 根据不同状态决定是否显示涟漪
  const shouldShowRipples = useMemo(() => {
    if (!enableRipples) return false;

    // 待机模式下总是显示涟漪
    if (entryStandbyMode) return true;

    // 聊天模式下，根据对话状态决定
    // 可以根据需要调整这个逻辑
    return !isAITalking && !isUserTalking;
  }, [enableRipples, entryStandbyMode, isAITalking, isUserTalking]);

  // 涟漪效果配置
  const rippleConfig = useMemo(() => {
    // 根据不同模式返回不同的配置
    const baseConfig = {
      dotCount,
      dotLifetime,
      dotInterval,
      radiusRange: { min: 170, max: 200 },
      sizeRange: { min: 12, max: 20 },
      color: 'rgba(46, 199, 167, 1)',
    };

    // 待机模式可能需要更活跃的涟漪效果
    if (entryStandbyMode) {
      return {
        ...baseConfig,
        dotCount: Math.max(dotCount, 20),
        dotInterval: Math.min(dotInterval, 100),
        color: 'rgba(46, 199, 167, 0.8)',
      };
    }

    // 聊天模式下的涟漪效果可能更温和
    return {
      ...baseConfig,
      dotCount: Math.min(dotCount, 12),
      dotInterval: Math.max(dotInterval, 150),
      color: 'rgba(46, 199, 167, 0.6)',
    };
  }, [entryStandbyMode, dotCount, dotLifetime, dotInterval]);

  return {
    shouldShowRipples,
    rippleConfig,
  };
}

export type { UseRippleAnimationOptions, UseRippleAnimationReturn };
